import { type SearchBox, searchBoxes } from "./search-box.js";
import { HandwritingPanel } from "./handwriting-panel.js";
import { HandwritingSource } from "./handwriting-source.js";

/**
 * Wiring the pen beside every search box on the page.
 *
 * A reader who knows a character by sight and cannot type it has nothing to put
 * in the box. The 部首 lookup answers that from a table and costs several
 * presses; this answers it from the shape, which is what the reader has.
 *
 * **The button is drawn hidden and shown here.** `partials/search-form.html`
 * writes it with `hidden` set, because a canvas needs a script and a button
 * that opens nothing is worse than no button. A reader whose bundle never
 * arrives sees the box exactly as it was.
 *
 * **One panel, however many boxes.** The dialog and the index behind it are
 * built once per page and every pen opens the same one, so a 词典 page with a
 * box on the bar, in the rail and in the article fetches the index once. See
 * `handwriting-panel.ts`.
 *
 * **The panel is built on the first press, not on load.** Every page carrying a
 * search box loads this, and almost every reader types. What the load costs is
 * this module finding the buttons and showing them.
 */

/** The pen inside the form, which the theme draws hidden. */
const penSelector = ".cb-search-handwriting";

/** Where the button carries the address of the published index. */
const indexAttribute = "cbHandwriting";

/** One pen, and the box it belongs to. */
interface HandwritingPen {
  readonly box: SearchBox;
  readonly pen: HTMLButtonElement;
}

/** Every box on the page whose form carries a pen. */
function handwritingPens(page: Document): readonly HandwritingPen[] {
  return searchBoxes(page).flatMap((box) => {
    const pen = box.form.querySelector<HTMLButtonElement>(penSelector);

    return pen === null ? [] : [{ box, pen }];
  });
}

/**
 * The published index's address, off whichever pen on the page carries it.
 *
 * The theme writes it on every pen it draws and the handler writes none on the
 * pen it draws, for the reason `search-markup.ts` gives. A search page carries
 * both, the navigation bar being the theme's, so one of the pens on any page
 * with a box on it names the file.
 */
function indexUrl(pens: readonly HandwritingPen[]): string {
  for (const { pen } of pens) {
    const url = pen.dataset[indexAttribute] ?? "";

    if (url !== "") {
      return url;
    }
  }

  return "";
}

/**
 * Shows every pen on the page and opens the pad from any of them.
 *
 * The panel is held in a variable the handlers close over rather than built
 * here, which is what makes the first press pay for it and every later press
 * free.
 */
export function startHandwriting(page: Document): void {
  const pens = handwritingPens(page);
  const url = indexUrl(pens);

  if (url === "") {
    return;
  }

  const source = new HandwritingSource({
    url,
    fetch: (input, init): Promise<Response> => fetch(input, init),
  });
  let panel: HandwritingPanel | undefined;

  for (const { box, pen } of pens) {
    pen.hidden = false;
    pen.addEventListener("click", () => {
      panel ??= new HandwritingPanel({ page, source, box });
      panel.open(box);
    });
  }
}
